import { ScrollMapsFacet } from './facetdata';

/**
 * A helper object used to group Scrolling areas into zones for creating gradients. Typically, these zones represent
 * areas where a certain percent of page views reached a given depth on the page. (e.g. 100%-90% of page views reached
 * a scroll depth of 10%).
 *
 * When in an array, the upper bound of the zone is the bottomScrollDepthPercent of the previous one (or 0 if none came
 * before):
 *
 *  {
 *      bottomScrollDepthPercent: 10 // Zone is Scroll Depth of 0% --> 10%
 *      pageViews: 100
 *  }
 *  {
 *      bottomScrollDepthPercent: 20 // Zone is Scroll Depth of 10% -->> 20%
 *      pageViews: 100
 *  }
 */
export interface ScrollZone {
  /**
   * The Scroll depth percent that is at the end of this zone.
   */
  bottomScrollDepthPercent: number;
  /**
   * The total number of page views.
   */
  pageViews: number;
}

// To create the Scroll Maps gradient, we will break the data into 10 zones. A zone
// will be created to represent 100 - 90%, 90-80%, etc of page views. The color map will then use these
// zones to create the gradient.
export function createScrollZones(facet: ScrollMapsFacet): ScrollZone[] {
  // If there are no page views at all, return an empty array. There are no Scroll Zones.
  if (facet.totalPageViews <= 0) {
    return [];
  }

  const scrollZones = [];
  let currentZonePercent = 100;
  for (let i = 0; i < 101; i++) {
    const val = facet.depthCounts[i] ?? 0;
    const currentPercentPageViews = (val * 100) / facet.totalPageViews;
    // If the currentPercentPageViews falls below the current zone, the current zone has come to an end.
    // The endpoint was the element that came before this.
    if (currentPercentPageViews < currentZonePercent) {
      scrollZones.push({
        bottomScrollDepthPercent: i - 1,
        pageViews: facet.depthCounts[i - 1] ?? 0,
      });
      // If the oldCurrentZone was 90, the new one would be 86 ==> 80 or 45 ==> 40. By calculating based
      // on the currentPercentVisitors, we can model large drops in percents with fewer ScrollZones.
      currentZonePercent = currentPercentPageViews - (currentPercentPageViews % 10);
    }
    // 0 is the last zone to fill in. If the current percent is zero and no users have reached this current depth
    // percent, exit.
    // This statement is an if statement versus if/else such that we can capture any zones that were closed by the 0.
    if (currentPercentPageViews == 0) {
      scrollZones.push({
        bottomScrollDepthPercent: i,
        pageViews: 0,
      });
      break;
    }
    // If we reached the final depth percentage and haven't reached the end of a zone, capture the data now.
    if (i == 100) {
      scrollZones.push({
        bottomScrollDepthPercent: 100,
        pageViews: val,
      });
    }
  }
  return scrollZones;
}

// An array of different colors arranged from hot (a lot of data) to cold (little data).
const COLORS = [
  '#EA1995',
  '#DE188E',
  '#DE239B',
  '#D42B9F',
  '#C734A4',
  '#B343AD',
  '#A54FB3',
  '#9958B8',
  '#8D61BE',
  '#7B6EC4',
  '#667ECD',
  '#5A88D3',
  '#4B94DA',
  '#4299DD',
  '#2FA9E6',
  '#20B5ED',
  '#1BC1F2',
  '#28C4F3',
  '#3AC8F4',
  '#4FCDF5',
  '#69D2F6',
  '#79D6F7',
  '#8FDAF7',
  '#A0DEF8',
  '#BBE4FA',
];
const NUM_GRADIENTS = COLORS.length;

/**
 * A Map used to display the colors of Scroll Maps based on how many page views reached a given depth.
 */
export interface ScrollMapsColorMap {
  /**
   * The maximum page view recorded.
   */
  maxPageView: number;

  /**
   * The minimum page view recorded.
   */
  minPageView: number;

  /**
   * maxPageView - minPageView, which is used to spread the distribution of colors evenly.
   */
  diff: number;

  /**
   * The list of Scroll Zones that group clusters of data with similar page views (e.g. depths with 100% - 90% of page
   * views have the same color).
   */
  scrollZones: ScrollZone[];
}

export function createScrollMapsColorMap(facet: ScrollMapsFacet): ScrollMapsColorMap {
  return createColorMapWithScrollZones(createScrollZones(facet));
}

export function createColorMapWithScrollZones(scrollZones: ScrollZone[]): ScrollMapsColorMap {
  const colorMap: ScrollMapsColorMap = {
    maxPageView: 0,
    minPageView: 0,
    diff: 0,
    scrollZones: [],
  };

  if (scrollZones.length === 0) {
    return colorMap;
  }

  // Sort Scroll Zones from lowest to greatest scroll depth percent. Once sorted, it should also be true that they
  // are sorted from greatest to least visit count.
  scrollZones.sort((a, b) => (a.bottomScrollDepthPercent > b.bottomScrollDepthPercent ? 1 : -1));
  for (let i = 0; i < scrollZones.length - 1; i++) {
    if (scrollZones[i]!.pageViews < scrollZones[i + 1]!.pageViews) {
      console.error(
        'Invalid ScrollZones passed in. Page views should be sorted from greatest to least.',
      );
      console.debug(scrollZones);
      return colorMap;
    }
  }

  if (
    scrollZones[scrollZones.length - 1]!.bottomScrollDepthPercent != 100 &&
    scrollZones[scrollZones.length - 1]!.pageViews != 0
  ) {
    console.error(
      'Final ScrollZone should either have a depth percent of 100% or page view count of 0.',
    );
    console.debug(scrollZones);
    return colorMap;
  }

  if (scrollZones.length == 1 && scrollZones[0]!.pageViews == 0) {
    console.error('Error. Can not produce a gradient for a single ScrollZone with no page views');
    console.debug(scrollZones);
    return colorMap;
  }

  colorMap.scrollZones = scrollZones;
  const minIndex = Math.min(NUM_GRADIENTS, scrollZones.length) - 1;
  const minElem = scrollZones[minIndex]!;

  colorMap.minPageView = minElem.pageViews;
  colorMap.maxPageView = scrollZones[0]!.pageViews;
  colorMap.diff = colorMap.maxPageView - colorMap.minPageView;
  return colorMap;
}

export function getScrollMapsColorForPageView(
  colorMap: ScrollMapsColorMap,
  pageViews: number,
): string {
  if (colorMap.diff === 0) {
    return COLORS[0]!;
  }

  if (pageViews == 0) {
    return 'transparent';
  }

  // 0.0 - 1.0 value
  const spread = (pageViews - colorMap.minPageView) / colorMap.diff;

  // 0 - NUM_GRADIENTS value
  const index = Math.round(spread * (NUM_GRADIENTS - 1));

  // invert the values to match the CSS
  // Subtract one to make sure the indices are 0 based
  const mapIdx = -(index - NUM_GRADIENTS) - 1;
  return COLORS[mapIdx]!;
}

// Produces a css LinearGradient string such as:
// linear-gradient(#EA1995 5%,#D42B9F 10%,#B343AD 17%,#8D61BE 25%,#5A88D3 35%,#4299DD 46%,#1BC1F2 58%,#4FCDF5 70%,#79D6F7 88%,#BBE4FA 100%)
export function getScrollMapsLinearGradient(colorMap: ScrollMapsColorMap): string {
  if (colorMap.scrollZones.length == 0) {
    return '';
  }

  let gradientString = 'linear-gradient(';

  // A gradient needs at least two colors. If we just have one, have the color span end to end to itself.
  // Based on the constructor checks, this is guaranteed to be a ScrollZone with a depth percent of 100 and a
  // visitor count greater than 0.
  if (colorMap.scrollZones.length == 1) {
    const gradient = getScrollMapsColorForPageView(colorMap, colorMap.scrollZones[0]!.pageViews);
    return `${gradientString + gradient} 0%, ${gradient} 100%)`;
  }

  colorMap.scrollZones.forEach((scrollZone, i) => {
    const gradient = getScrollMapsColorForPageView(colorMap, scrollZone.pageViews);
    gradientString = `${gradientString + gradient} ${scrollZone.bottomScrollDepthPercent}%,`;
  });

  return `${gradientString.slice(0, gradientString.length - 1)})`;
}
